/**
 * TasksBoardView — the project-management board.
 *
 * Tasks are the PM primitive layered over Workflows: a to-do with an
 * owner, due date, checklist of subtasks, optional recurrence, and an
 * append-only timeline. The board is a four-column kanban (todo →
 * in-progress → blocked → done); cards show assignee, due date (with an
 * overdue signal), priority, checklist progress, recurrence, and the
 * account / workflow the task supports. Clicking a card opens a detail
 * drawer that drives status / assignee / checklist mutations.
 *
 * Wired to the tasks adapter (src/lib/db/tasks.ts) — localStorage in
 * dev, Firestore in prod. Magenta-only accent; Lucide icons only.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Archive,
  Plus,
  X,
  Inbox,
  Calendar as CalendarIcon,
  CalendarClock,
  AlertTriangle,
  User as UserIcon,
  Repeat,
  CheckSquare,
  Square,
  ListChecks,
  Trash2,
  CircleDashed,
  Loader2,
  Ban,
  CheckCircle2,
  Send,
  Link2,
  LayoutGrid,
  List,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  PenLine,
  Clock,
  MessageSquare,
  Paperclip,
  type LucideIcon,
} from 'lucide-react';
import { GlassPanel } from '../components/GlassPanel';
import { MentionTextarea } from '../components/MentionTextarea';
import { CriticalPathPanel } from '../components/CriticalPathPanel';
import { LoadingState } from '../components/States';
import { C } from '../lib/tokens';
import {
  TASK_DETAIL_OVERLAY,
  TASK_DETAIL_DRAWER,
  TASK_DETAIL_PANEL,
  TASK_DETAIL_HEADER,
  TASK_DETAIL_BODY,
  TASK_DETAIL_FOOTER,
  TASKS_VIEW_SCROLLER,
} from '../lib/taskDetailLayout';
import { useAuth } from '../lib/auth/AuthProvider';
import {
  TASK_ASSIGNEES,
  addChecklistItem,
  addTaskAttachment,
  addTaskComment,
  addTaskTimeEntry,
  bootstrapTasksIfEmpty,
  createTask,
  removeChecklistItem,
  removeTaskAttachment,
  removeTaskTimeEntry,
  setTaskStatus,
  subscribeTasks,
  toggleChecklistItem,
  updateTask,
} from '../lib/db/tasks';
import { parseMentions } from '../lib/crm/mentions';
import { queueMentionEmails } from '../lib/crm/notifyMentions';
import { FileAttachButton } from '../components/FileAttachButton';
import { subscribeTickets } from '../lib/db/tickets';
import { subscribeRoadmap } from '../lib/db/roadmap';
import { milestonesLinkingTask } from '../lib/crm/milestones';
import * as argoWorker from '../lib/agent/argoWorker';
import {
  ASSIST_MODE_LABEL,
  newChecklistSteps,
  requestAssist,
  suggestForTask,
  type AssistMode,
  type TaskAssist,
} from '../lib/crm/taskAgent';
import type {
  Project,
  ProjectTask,
  RoadmapItem,
  TaskPriority,
  TaskRecurrence,
  TaskStatus,
  Ticket,
} from '../lib/db/types';
import { subscribeProjects } from '../lib/db/projects';

// --- Status / priority presentation ----------------------------------------

const STATUS_ORDER: TaskStatus[] = ['todo', 'in-progress', 'blocked', 'done'];

const STATUS_META: Record<
  TaskStatus,
  { label: string; pill: string; icon: LucideIcon }
> = {
  todo: { label: 'To do', pill: 'glass-pill', icon: CircleDashed },
  'in-progress': {
    label: 'In progress',
    pill: 'glass-pill glass-pill--magenta',
    icon: Loader2,
  },
  blocked: { label: 'Blocked', pill: 'glass-pill glass-pill--orange', icon: Ban },
  done: { label: 'Done', pill: 'glass-pill glass-pill--green', icon: CheckCircle2 },
};

const PRIORITY_META: Record<TaskPriority, { label: string; pill: string }> = {
  urgent: { label: 'Urgent', pill: 'glass-pill glass-pill--orange' },
  high: { label: 'High', pill: 'glass-pill glass-pill--magenta' },
  medium: { label: 'Medium', pill: 'glass-pill glass-pill--blue' },
  low: { label: 'Low', pill: 'glass-pill' },
};

const PRIORITIES: TaskPriority[] = ['urgent', 'high', 'medium', 'low'];

const RECURRENCE_META: Record<TaskRecurrence, string> = {
  none: 'One-time',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};

const RECURRENCES: TaskRecurrence[] = ['none', 'daily', 'weekly', 'monthly'];

function isOverdue(t: ProjectTask): boolean {
  if (!t.dueDate || t.status === 'done') return false;
  const d = new Date(`${t.dueDate}T23:59:59`).getTime();
  return Number.isFinite(d) && d < Date.now();
}

function fmtRelative(iso: string): string {
  try {
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 14) return `${days}d ago`;
    return fmtDate(iso);
  } catch {
    return iso;
  }
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function checklistProgress(t: ProjectTask): { done: number; total: number } {
  return {
    done: t.checklist.filter((c) => c.done).length,
    total: t.checklist.length,
  };
}

// ===========================================================================

interface TasksBoardViewProps {
  /** Task to open on arrival (from the ⌘K command palette). The nonce
   *  lets the same task be re-focused twice in a row. */
  focus?: { id: string; nonce: number } | null;
}

export function TasksBoardView({ focus = null }: TasksBoardViewProps) {
  const { user } = useAuth();
  const canMutate = user?.role !== 'viewer';
  const actor = user?.displayName?.split(' ')[0] ?? 'You';

  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  // First-snapshot flag so the board shows a skeleton, not empty columns,
  // before the store has answered.
  const [loaded, setLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'board' | 'list' | 'calendar'>('board');
  const [meMode, setMeMode] = useState(false);
  const lastFocusNonce = useRef(0);
  const [listSort, setListSort] = useState<'due' | 'priority' | 'assignee'>('due');
  const [quickAddTitle, setQuickAddTitle] = useState('');
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  useEffect(() => {
    let active = true;
    bootstrapTasksIfEmpty().catch(() => undefined);
    const unsub = subscribeTasks((records) => {
      if (active) {
        // Archived tasks are kept in the store but hidden from the board
        // and every roll-up derived from this list.
        setTasks(
          records
            .filter((t) => !t.archived)
            .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1)),
        );
        setLoaded(true);
      }
    });
    const unsubP = subscribeProjects((r) => active && setProjects(r));
    return () => {
      active = false;
      unsub();
      unsubP();
    };
  }, []);

  // Entity quick-jump: open the task chosen in the ⌘K palette. Clear the
  // board filters so the card is also visible in context behind the drawer.
  useEffect(() => {
    if (!focus || focus.nonce === lastFocusNonce.current) return;
    lastFocusNonce.current = focus.nonce;
    setSelectedId(focus.id);
    setAssigneeFilter('all');
    setMeMode(false);
  }, [focus]);

  // Match assignee to user's first name (case-insensitive).
  // e.g., assignee "Trent" matches displayName "Trent Hughes"
  const isCurrentUserAssignee = (assignee: string | null): boolean => {
    if (!assignee || !user?.displayName) return false;
    const userFirstName = user.displayName.toLowerCase().split(' ')[0];
    return assignee.toLowerCase().startsWith(userFirstName);
  };

  const assignees = useMemo(() => {
    const set = new Set<string>();
    for (const t of tasks) if (t.assignee) set.add(t.assignee);
    return [...set].sort();
  }, [tasks]);

  const filtered = useMemo(() => {
    let result = tasks;
    if (meMode) {
      result = result.filter((t) => isCurrentUserAssignee(t.assignee));
    } else if (assigneeFilter !== 'all') {
      result = result.filter((t) => t.assignee === assigneeFilter);
    }
    return result;
  }, [tasks, assigneeFilter, meMode, user?.displayName]);

  const columns = useMemo(() => {
    const byStatus: Record<TaskStatus, ProjectTask[]> = {
      todo: [],
      'in-progress': [],
      blocked: [],
      done: [],
    };
    for (const t of filtered) byStatus[t.status].push(t);
    // Within a column: overdue first, then by due date, then recent.
    for (const s of STATUS_ORDER) {
      byStatus[s].sort((a, b) => {
        const ao = isOverdue(a) ? 0 : 1;
        const bo = isOverdue(b) ? 0 : 1;
        if (ao !== bo) return ao - bo;
        if (a.dueDate && b.dueDate) return a.dueDate < b.dueDate ? -1 : 1;
        if (a.dueDate) return -1;
        if (b.dueDate) return 1;
        return a.updatedAt < b.updatedAt ? 1 : -1;
      });
    }
    return byStatus;
  }, [filtered]);

  const selected = useMemo(
    () => tasks.find((t) => t.id === selectedId) ?? null,
    [tasks, selectedId],
  );

  const overdueCount = useMemo(
    () => filtered.filter(isOverdue).length,
    [filtered],
  );

  // Parse lightweight tokens from quick-add input: !priority, @assignee, date.
  const parseQuickAdd = (input: string): { title: string; priority: TaskPriority; assignee: string | null; dueDate: string | null } => {
    let title = input;
    let priority: TaskPriority = 'medium';
    let assignee: string | null = null;
    let dueDate: string | null = null;

    // Extract priority: !urgent, !high, !low (!medium is ignored, it's default)
    const priorityMatch = title.match(/!urgent|!high|!low/);
    if (priorityMatch) {
      const token = priorityMatch[0];
      priority = (token.slice(1) as TaskPriority) || 'medium';
      title = title.replace(token, '').trim();
    }

    // Extract assignee: @Name (match against TASK_ASSIGNEES for case-insensitive match)
    const assigneeMatch = title.match(/@(\w+)/);
    if (assigneeMatch) {
      const namePrefix = assigneeMatch[1];
      const match = TASK_ASSIGNEES.find((a) =>
        a.toLowerCase().startsWith(namePrefix.toLowerCase()),
      );
      if (match) {
        assignee = match;
        title = title.replace(assigneeMatch[0], '').trim();
      }
    }

    // Extract date: "tomorrow", "friday", or ISO date (YYYY-MM-DD)
    const dateMatch = title.match(
      /\btomorrow\b|\b(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b|(\d{4}-\d{2}-\d{2})/i,
    );
    if (dateMatch) {
      const token = dateMatch[0].toLowerCase();
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (token === 'tomorrow') {
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        dueDate = tomorrow.toISOString().slice(0, 10);
      } else if (/^(monday|tuesday|wednesday|thursday|friday|saturday|sunday)$/.test(token)) {
        const dayMap: Record<string, number> = {
          monday: 1,
          tuesday: 2,
          wednesday: 3,
          thursday: 4,
          friday: 5,
          saturday: 6,
          sunday: 0,
        };
        const targetDay = dayMap[token];
        const nextDay = new Date(today);
        let dayDiff = (targetDay - today.getDay() + 7) % 7;
        if (dayDiff === 0) dayDiff = 7; // Next occurrence, not today
        nextDay.setDate(nextDay.getDate() + dayDiff);
        dueDate = nextDay.toISOString().slice(0, 10);
      } else if (/^\d{4}-\d{2}-\d{2}$/.test(token)) {
        dueDate = token;
      }
      title = title.replace(dateMatch[0], '').trim();
    }

    return { title, priority, assignee, dueDate };
  };

  const onQuickAdd = async () => {
    const trimmed = quickAddTitle.trim();
    if (!trimmed) return;

    const { title, priority, assignee, dueDate } = parseQuickAdd(trimmed);
    if (!title) return;

    try {
      await createTask({
        title,
        priority,
        assignee,
        dueDate,
        createdBy: actor,
      });
      setQuickAddTitle('');
    } catch (err) {
      console.error('Failed to create task:', err);
    }
  };

  return (
    // The Projects shell is height-locked (.app-shell--fixed clips its
    // scroll container), so this view owns its own vertical scroll —
    // otherwise the board/list/calendar below the fold is unreachable.
    <div style={TASKS_VIEW_SCROLLER}>
      {/* Header */}
      <GlassPanel variant="subtle" animate={false} style={{ padding: '14px 18px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <span className="t-eyebrow">Projects · Tasks</span>
            <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>
              Project tasks
            </h2>
            <p className="t-meta" style={{ margin: '4px 0 0', color: C.textMuted }}>
              To-dos with owners, due dates, checklists, and recurring cadence —
              the work behind each account&apos;s delivery.
              {overdueCount > 0 && (
                <span style={{ color: C.orangeWarm, fontWeight: 700 }}>
                  {' '}· {overdueCount} overdue
                </span>
              )}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {/* View toggle: Board / List */}
            <div style={{ display: 'inline-flex', gap: 4, padding: '2px 4px', background: 'rgba(255,255,255,0.04)', borderRadius: 6 }}>
              <button
                type="button"
                onClick={() => setViewMode('board')}
                className={viewMode === 'board' ? 'glass-btn glass-btn--primary' : 'glass-btn'}
                style={{ padding: '4px 8px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <LayoutGrid size={13} />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'glass-btn glass-btn--primary' : 'glass-btn'}
                style={{ padding: '4px 8px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <List size={13} />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('calendar')}
                className={viewMode === 'calendar' ? 'glass-btn glass-btn--primary' : 'glass-btn'}
                style={{ padding: '4px 8px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <CalendarIcon size={13} />
              </button>
            </div>

            {/* Me mode filter */}
            {user?.displayName && (
              <button
                type="button"
                onClick={() => setMeMode(!meMode)}
                className={meMode ? 'glass-pill glass-pill--magenta' : 'glass-pill'}
                style={{ fontSize: '0.75rem', padding: '4px 10px', fontWeight: meMode ? 700 : 400 }}
              >
                Me
              </button>
            )}

            {/* Assignee filter (hide in me mode) */}
            {!meMode && (
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span className="t-eyebrow" style={{ whiteSpace: 'nowrap' }}>Assignee</span>
                <select
                  className="glass-input"
                  value={assigneeFilter}
                  onChange={(e) => setAssigneeFilter(e.target.value)}
                  style={{ padding: '6px 10px' }}
                >
                  <option value="all">Everyone</option>
                  {assignees.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {canMutate && (
              <button
                className="glass-btn glass-btn--primary"
                onClick={() => setShowForm(true)}
              >
                <Plus size={13} /> New task
              </button>
            )}
          </div>
        </div>
      </GlassPanel>

      {/* Critical path & slips — the PM dependency engine made visible.
          Runs over ALL tasks (not the filtered slice) so a slip can never
          be hidden by an assignee filter. */}
      {loaded && <CriticalPathPanel tasks={tasks} canScan={canMutate} />}

      {/* Quick-add input */}
      {canMutate && (
        <GlassPanel variant="subtle" animate={false} style={{ padding: '12px 18px' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              className="glass-input"
              placeholder="Quick add: Title @Name !priority tomorrow"
              value={quickAddTitle}
              onChange={(e) => setQuickAddTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  onQuickAdd();
                }
              }}
              style={{ flex: 1 }}
            />
            <button
              type="button"
              className="glass-btn glass-btn--primary"
              onClick={onQuickAdd}
              disabled={!quickAddTitle.trim()}
            >
              <Plus size={13} />
            </button>
          </div>
          <p className="t-meta" style={{ margin: '6px 0 0', color: C.textDim }}>
            Tokens: @Name sets assignee, !urgent/!high/!low sets priority, tomorrow/friday/YYYY-MM-DD sets due date
          </p>
        </GlassPanel>
      )}

      {/* Board or List view */}
      {!loaded ? (
        <LoadingState rows={4} label="Loading tasks" />
      ) : viewMode === 'board' ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: 12,
            alignItems: 'start',
          }}
        >
          {STATUS_ORDER.map((status) => (
            <TaskColumn
              key={status}
              status={status}
              tasks={columns[status]}
              allTasks={tasks}
              onOpen={(id) => setSelectedId(id)}
              canMutate={canMutate}
              onDropTask={(id, s) => {
                void setTaskStatus(id, s, actor);
              }}
            />
          ))}
        </div>
      ) : viewMode === 'list' ? (
        <TaskListView
          tasks={filtered}
          listSort={listSort}
          onChangeSort={setListSort}
          onOpen={(id) => setSelectedId(id)}
        />
      ) : (
        <TaskCalendarView
          tasks={filtered}
          month={calendarMonth}
          onChangeMonth={setCalendarMonth}
          onOpen={(id) => setSelectedId(id)}
        />
      )}

      {selected && (
        <TaskDetail
          task={selected}
          allTasks={tasks}
          projects={projects}
          canMutate={canMutate}
          actor={actor}
          onClose={() => setSelectedId(null)}
          onDeleted={() => setSelectedId(null)}
        />
      )}

      {showForm && (
        <TaskForm
          createdBy={actor}
          projects={projects}
          onClose={() => setShowForm(false)}
          onCreated={(id) => {
            setShowForm(false);
            setSelectedId(id);
          }}
        />
      )}
    </div>
  );
}

// --- Calendar View -------------------------------------------------------

/**
 * Month grid view: current month with Mon–Sun headers, day cells listing
 * tasks by dueDate (compact: priority dot + truncated title, max 3 + "+n more"),
 * today highlighted. Respects the Me-mode filter. Tasks without dueDate are
 * excluded (shown as a count note).
 */
function TaskCalendarView({
  tasks,
  month,
  onChangeMonth,
  onOpen,
}: {
  tasks: ProjectTask[];
  month: Date;
  onChangeMonth: (d: Date) => void;
  onOpen: (id: string) => void;
}) {
  const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // Get the first day of the month and number of days.
  const year = month.getFullYear();
  const monthIdx = month.getMonth();
  const firstDay = new Date(year, monthIdx, 1);
  const lastDay = new Date(year, monthIdx + 1, 0);
  const daysInMonth = lastDay.getDate();

  // Determine which weekday (0–6, Mon–Sun) the first day falls on.
  // JavaScript getDay returns 0=Sunday, so we adjust.
  const startWeekday = (firstDay.getDay() + 6) % 7;

  // Build a grid of weeks (each row is a week, starting Monday).
  const weeks: (number | null)[][] = [];
  let week: (number | null)[] = Array(startWeekday).fill(null);
  for (let d = 1; d <= daysInMonth; d++) {
    week.push(d);
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }
  if (week.length > 0) {
    while (week.length < 7) week.push(null);
    weeks.push(week);
  }

  // Map tasks to days.
  const tasksByDay = new Map<number, ProjectTask[]>();
  const unscheduledCount = tasks.filter((t) => !t.dueDate).length;
  for (const t of tasks) {
    if (!t.dueDate) continue;
    const d = parseInt(t.dueDate.split('-')[2], 10);
    if (!Number.isNaN(d) && d >= 1 && d <= daysInMonth) {
      if (!tasksByDay.has(d)) tasksByDay.set(d, []);
      tasksByDay.get(d)!.push(t);
    }
  }

  // Sort tasks within each day by priority.
  const PRIORITY_RANK: Record<string, number> = {
    urgent: 0,
    high: 1,
    medium: 2,
    low: 3,
  };
  for (const dayTasks of tasksByDay.values()) {
    dayTasks.sort(
      (a, b) => (PRIORITY_RANK[a.priority] ?? 99) - (PRIORITY_RANK[b.priority] ?? 99),
    );
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isCurrentMonth =
    today.getFullYear() === year && today.getMonth() === monthIdx;
  const todayDate = isCurrentMonth ? today.getDate() : -1;

  const prevMonth = () => {
    const prev = new Date(month);
    prev.setMonth(prev.getMonth() - 1);
    onChangeMonth(prev);
  };

  const nextMonth = () => {
    const next = new Date(month);
    next.setMonth(next.getMonth() + 1);
    onChangeMonth(next);
  };

  const monthName = month.toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Month header and nav */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <button
          type="button"
          className="glass-btn"
          onClick={prevMonth}
          style={{ padding: '4px 8px', display: 'inline-flex', alignItems: 'center' }}
          title="Previous month"
        >
          <ChevronLeft size={16} />
        </button>
        <h3 className="t-section-title" style={{ margin: 0, flex: 1, textAlign: 'center' }}>
          {monthName}
        </h3>
        <button
          type="button"
          className="glass-btn"
          onClick={nextMonth}
          style={{ padding: '4px 8px', display: 'inline-flex', alignItems: 'center' }}
          title="Next month"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Unscheduled count note */}
      {unscheduledCount > 0 && (
        <p className="t-meta" style={{ margin: 0, color: C.textMuted, fontSize: '0.875rem' }}>
          {unscheduledCount} unscheduled task{unscheduledCount === 1 ? '' : 's'} not shown
        </p>
      )}

      {/* Calendar grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
        {/* Day headers */}
        {DAYS_OF_WEEK.map((day) => (
          <div
            key={day}
            style={{
              padding: '8px',
              textAlign: 'center',
              fontWeight: 600,
              fontSize: '0.75rem',
              color: C.textMuted,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            {day}
          </div>
        ))}

        {/* Day cells */}
        {weeks.map((week, weekIdx) =>
          week.map((dayNum, dayIdx) => {
            const dayTasks = dayNum ? tasksByDay.get(dayNum) ?? [] : [];
            const isToday = dayNum === todayDate;
            const inOtherMonth = !dayNum;

            return (
              <div
                key={`${weekIdx}-${dayIdx}`}
                style={{
                  minHeight: 100,
                  padding: '8px',
                  borderRadius: 8,
                  border: inOtherMonth ? 'none' : `1px solid rgba(255,255,255,0.06)`,
                  background: isToday
                    ? 'rgba(187,65,152,0.12)'
                    : inOtherMonth
                      ? 'transparent'
                      : 'rgba(255,255,255,0.02)',
                  cursor: dayNum ? 'default' : 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                }}
              >
                {dayNum && (
                  <>
                    <div
                      style={{
                        fontWeight: isToday ? 700 : 500,
                        fontSize: '0.875rem',
                        color: isToday ? C.magenta : C.text,
                      }}
                    >
                      {dayNum}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 0 }}>
                      {dayTasks.slice(0, 3).map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => onOpen(t.id)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '3px 6px',
                            borderRadius: 4,
                            background: 'rgba(255,255,255,0.06)',
                            border: 'none',
                            cursor: 'pointer',
                            minWidth: 0,
                            textAlign: 'left',
                            fontSize: '0.7rem',
                          }}
                        >
                          <span
                            aria-hidden="true"
                            style={{
                              flex: '0 0 4px',
                              height: 4,
                              borderRadius: 2,
                              background:
                                t.priority === 'urgent'
                                  ? C.orangeWarm
                                  : t.priority === 'high'
                                    ? C.magenta
                                    : t.priority === 'medium'
                                      ? C.blueElectric
                                      : C.textDim,
                            }}
                          />
                          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: C.text }}>
                            {t.title}
                          </span>
                        </button>
                      ))}
                      {dayTasks.length > 3 && (
                        <span
                          style={{
                            fontSize: '0.65rem',
                            color: C.textDim,
                            paddingLeft: 6,
                          }}
                        >
                          +{dayTasks.length - 3} more
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          }),
        )}
      </div>
    </div>
  );
}

// --- List View ---------------------------------------------------------------

/** Helper to format overdue/today due dates with labels and color. */
function DueDateBadge({ dueDate, overdue }: { dueDate: string | null; overdue: boolean }) {
  if (!dueDate) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueObj = new Date(`${dueDate}T00:00:00`);

  const isToday =
    dueObj.getFullYear() === today.getFullYear() &&
    dueObj.getMonth() === today.getMonth() &&
    dueObj.getDate() === today.getDate();

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        color: isToday ? C.magenta : overdue ? C.orangeWarm : undefined,
        fontWeight: isToday || overdue ? 700 : undefined,
      }}
    >
      <CalendarClock size={12} />
      {isToday ? 'today' : overdue ? `${Math.ceil((today.getTime() - dueObj.getTime()) / 86400000)}d overdue` : fmtDate(dueDate)}
    </span>
  );
}

function TaskListView({
  tasks,
  listSort,
  onChangeSort,
  onOpen,
}: {
  tasks: ProjectTask[];
  listSort: 'due' | 'priority' | 'assignee';
  onChangeSort: (sort: 'due' | 'priority' | 'assignee') => void;
  onOpen: (id: string) => void;
}) {
  // Separate done from non-done; sort non-done by the selected criterion
  const nonDone = useMemo(() => {
    const list = tasks.filter((t) => t.status !== 'done');
    const sorted = [...list];
    if (listSort === 'due') {
      sorted.sort((a, b) => {
        const aOverdue = isOverdue(a);
        const bOverdue = isOverdue(b);
        if (aOverdue !== bOverdue) return aOverdue ? -1 : 1;
        if (a.dueDate && b.dueDate) return a.dueDate < b.dueDate ? -1 : 1;
        if (a.dueDate) return -1;
        if (b.dueDate) return 1;
        return a.updatedAt < b.updatedAt ? 1 : -1;
      });
    } else if (listSort === 'priority') {
      const priorityOrder: Record<TaskPriority, number> = {
        urgent: 0,
        high: 1,
        medium: 2,
        low: 3,
      };
      sorted.sort((a, b) => {
        if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        }
        const aOverdue = isOverdue(a);
        const bOverdue = isOverdue(b);
        if (aOverdue !== bOverdue) return aOverdue ? -1 : 1;
        return a.updatedAt < b.updatedAt ? 1 : -1;
      });
    } else if (listSort === 'assignee') {
      sorted.sort((a, b) => {
        const aName = a.assignee || '~';
        const bName = b.assignee || '~';
        if (aName !== bName) return aName < bName ? -1 : 1;
        return a.updatedAt < b.updatedAt ? 1 : -1;
      });
    }
    return sorted;
  }, [tasks, listSort]);

  const done = useMemo(() => tasks.filter((t) => t.status === 'done'), [tasks]);
  const [expandDone, setExpandDone] = useState(false);

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {/* Sort controls */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          padding: '0 4px',
        }}
      >
        <span className="t-eyebrow">Sort by:</span>
        {(['due', 'priority', 'assignee'] as const).map((sortOption) => (
          <button
            key={sortOption}
            type="button"
            onClick={() => onChangeSort(sortOption)}
            className={
              listSort === sortOption
                ? 'glass-pill glass-pill--magenta'
                : 'glass-pill'
            }
            style={{
              fontSize: '0.75rem',
              padding: '4px 10px',
              fontWeight: listSort === sortOption ? 700 : 400,
              textTransform: 'capitalize',
            }}
          >
            {sortOption === 'due'
              ? 'Due date'
              : sortOption === 'priority'
                ? 'Priority'
                : 'Assignee'}
          </button>
        ))}
      </div>

      {/* Non-done tasks */}
      <div style={{ display: 'grid', gap: 1 }}>
        {nonDone.length === 0 ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 4,
              padding: '18px 0',
              color: C.textDim,
            }}
          >
            <Inbox size={16} strokeWidth={1.75} />
            <span className="t-meta">No active tasks</span>
          </div>
        ) : (
          nonDone.map((task) => (
            <TaskListRow
              key={task.id}
              task={task}
              onOpen={() => onOpen(task.id)}
            />
          ))
        )}
      </div>

      {/* Done section (collapsed by default) */}
      {done.length > 0 && (
        <div style={{ display: 'grid', gap: 1, marginTop: 12 }}>
          <button
            type="button"
            onClick={() => setExpandDone(!expandDone)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 12px',
              background: 'transparent',
              border: 'none',
              color: C.textMuted,
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          >
            <span style={{ color: C.textDim }}>
              {expandDone ? '▼' : '▶'}
            </span>
            Done ({done.length})
          </button>
          {expandDone && (
            <div style={{ display: 'grid', gap: 1, marginTop: 2 }}>
              {done.map((task) => (
                <TaskListRow
                  key={task.id}
                  task={task}
                  onOpen={() => onOpen(task.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** A single row in the list view. */
function TaskListRow({ task, onOpen }: { task: ProjectTask; onOpen: () => void }) {
  const overdue = isOverdue(task);
  const { done, total } = checklistProgress(task);
  const pMeta = PRIORITY_META[task.priority];

  return (
    <button
      type="button"
      className="glass-row"
      onClick={onOpen}
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto auto auto auto auto',
        gap: 12,
        alignItems: 'center',
        textAlign: 'left',
        width: '100%',
        padding: '10px 12px',
      }}
    >
      {/* Priority dot */}
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: 99,
          background: pMeta.pill.includes('--orange')
            ? C.orangeWarm
            : pMeta.pill.includes('--magenta')
              ? C.magenta
              : pMeta.pill.includes('--blue')
                ? C.blueElectric
                : C.textDim,
          flex: '0 0 auto',
        }}
      />

      {/* Title */}
      <span
        className="t-body"
        style={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          textDecoration: task.status === 'done' ? 'line-through' : undefined,
          color: task.status === 'done' ? C.textDim : C.text,
        }}
      >
        {task.title}
      </span>

      {/* Assignee */}
      <span className="t-meta" style={{ flex: '0 0 auto', whiteSpace: 'nowrap' }}>
        {task.assignee ?? '—'}
      </span>

      {/* Due date with overdue label */}
      <span className="t-meta" style={{ flex: '0 0 auto', whiteSpace: 'nowrap' }}>
        <DueDateBadge dueDate={task.dueDate} overdue={overdue} />
      </span>

      {/* Checklist progress */}
      {total > 0 && (
        <span className="t-meta" style={{ flex: '0 0 auto', whiteSpace: 'nowrap' }}>
          {done}/{total}
        </span>
      )}

      {/* Status pill */}
      <span
        className={STATUS_META[task.status].pill}
        style={{ flex: '0 0 auto', fontSize: '0.7rem' }}
      >
        {STATUS_META[task.status].label}
      </span>
    </button>
  );
}

// --- Column -----------------------------------------------------------------

function TaskColumn({
  status,
  tasks,
  allTasks,
  onOpen,
  canMutate = false,
  onDropTask,
}: {
  status: TaskStatus;
  tasks: ProjectTask[];
  /** Full board — lets each card's "Argo suggests" hint name its blockers. */
  allTasks: ProjectTask[];
  onOpen: (id: string) => void;
  /** Gates drag-and-drop (viewers can't move cards). */
  canMutate?: boolean;
  /** Drop a task into this column's status. */
  onDropTask?: (taskId: string, status: TaskStatus) => void;
}) {
  const meta = STATUS_META[status];
  const Icon = meta.icon;
  const [over, setOver] = useState(false);
  const dndProps =
    canMutate && onDropTask
      ? {
          onDragOver: (e: React.DragEvent) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            if (!over) setOver(true);
          },
          onDragLeave: () => setOver(false),
          onDrop: (e: React.DragEvent) => {
            e.preventDefault();
            setOver(false);
            const id = e.dataTransfer.getData('text/plain');
            if (id) onDropTask(id, status);
          },
        }
      : {};
  return (
    <GlassPanel
      variant="subtle"
      animate={false}
      style={{
        padding: 12,
        outline: over ? `2px dashed ${C.magenta}` : '2px dashed transparent',
        outlineOffset: -2,
        transition: 'outline-color 120ms ease',
      }}
      {...dndProps}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 10,
        }}
      >
        <Icon size={13} style={{ color: C.textMuted }} />
        <span className="t-eyebrow">{meta.label}</span>
        <span className="t-meta" style={{ color: C.textMuted }}>
          {tasks.length}
        </span>
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        {tasks.length === 0 ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 4,
              padding: '18px 0',
              color: C.textDim,
            }}
          >
            <Inbox size={16} strokeWidth={1.75} />
            <span className="t-meta">Empty</span>
          </div>
        ) : (
          tasks.map((t) => (
            <TaskCard key={t.id} task={t} allTasks={allTasks} onOpen={() => onOpen(t.id)} draggable={canMutate} />
          ))
        )}
      </div>
    </GlassPanel>
  );
}

// --- Card -------------------------------------------------------------------

function TaskCard({
  task,
  allTasks = [],
  onOpen,
  draggable = false,
}: {
  task: ProjectTask;
  allTasks?: ProjectTask[];
  onOpen: () => void;
  /** When true, the card can be dragged between board columns. */
  draggable?: boolean;
}) {
  const pMeta = PRIORITY_META[task.priority];
  const overdue = isOverdue(task);
  const { done, total } = checklistProgress(task);
  // "Argo suggests" — pure, instant hint compiled from the task's own
  // live signals (see lib/crm/taskAgent). Null when nothing is notable.
  const hint = useMemo(
    () => suggestForTask(task, { allTasks }),
    [task, allTasks],
  );

  return (
    <button
      type="button"
      className="glass-row"
      onClick={onOpen}
      draggable={draggable}
      onDragStart={
        draggable
          ? (e) => {
              e.dataTransfer.setData('text/plain', task.id);
              e.dataTransfer.effectAllowed = 'move';
            }
          : undefined
      }
      style={{
        display: 'grid',
        gap: 8,
        textAlign: 'left',
        width: '100%',
        padding: 12,
        cursor: draggable ? 'grab' : 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
        <span
          className="t-card-title"
          style={{ flex: 1, minWidth: 0, lineHeight: 1.3 }}
        >
          {task.title}
        </span>
        <span className={pMeta.pill} style={{ flex: '0 0 auto' }}>
          {pMeta.label}
        </span>
      </div>

      {(task.accountName || task.workflowName) && (
        <span
          className="t-meta"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            color: C.textMuted,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          <Link2 size={11} />
          {[task.accountName, task.workflowName].filter(Boolean).join(' · ')}
        </span>
      )}

      <div
        className="t-meta"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <UserIcon size={11} /> {task.assignee ?? 'Unassigned'}
        </span>
        {task.dueDate && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              color: overdue ? C.orangeWarm : undefined,
              fontWeight: overdue ? 700 : undefined,
            }}
          >
            {overdue ? <AlertTriangle size={11} /> : <CalendarClock size={11} />}
            {fmtDate(task.dueDate)}
            {overdue && (
              <span style={{ fontSize: '0.75rem', marginLeft: 2 }}>
                {Math.ceil((Date.now() - new Date(`${task.dueDate}T23:59:59`).getTime()) / 86400000)}d overdue
              </span>
            )}
          </span>
        )}
        {task.recurrence !== 'none' && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Repeat size={11} /> {RECURRENCE_META[task.recurrence]}
          </span>
        )}
        {total > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <ListChecks size={11} /> {done}/{total}
          </span>
        )}
      </div>

      {/* "Argo suggests" — open the card to act on it with one click. */}
      {hint && (
        <span
          className="t-meta"
          title="Argo suggests — open the task and ask Argo to act on it"
          style={{
            display: 'inline-flex',
            alignItems: 'flex-start',
            gap: 5,
            color: C.magenta,
          }}
        >
          <Sparkles size={11} style={{ flex: '0 0 auto', marginTop: 2 }} />
          <span style={{ minWidth: 0 }}>{hint.text}</span>
        </span>
      )}
    </button>
  );
}

// --- Detail drawer ----------------------------------------------------------

export function TaskDetail({
  task,
  allTasks = [],
  projects = [],
  canMutate,
  actor,
  onClose,
  onDeleted,
}: {
  task: ProjectTask;
  /** Full board — lets the drawer's Argo hint name blocking tasks. */
  allTasks?: ProjectTask[];
  projects?: Project[];
  canMutate: boolean;
  actor: string;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [newItem, setNewItem] = useState('');
  const overdue = isOverdue(task);

  // Live tickets + roadmap, subscribed only while the drawer is open —
  // they power the "Linked ticket" select and the milestone rollup
  // chips without making the whole board pay for the data.
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [roadmap, setRoadmap] = useState<RoadmapItem[]>([]);
  useEffect(() => {
    const unsubs = [subscribeTickets(setTickets), subscribeRoadmap(setRoadmap)];
    return () => unsubs.forEach((u) => u());
  }, []);
  const linkedTicket = useMemo(
    () => tickets.find((t) => t.id === task.ticketId) ?? null,
    [tickets, task.ticketId],
  );
  const linkedMilestones = useMemo(
    () => milestonesLinkingTask(roadmap, task.id),
    [roadmap, task.id],
  );

  const onTicket = async (ticketId: string) => {
    setBusy(true);
    try {
      await updateTask(task.id, { ticketId: ticketId || undefined }, actor);
    } finally {
      setBusy(false);
    }
  };

  const onProject = async (projectId: string) => {
    setBusy(true);
    try {
      const proj = projects.find((p) => p.id === projectId);
      await updateTask(
        task.id,
        proj
          ? { projectId: proj.id, projectName: proj.title, accountName: proj.clientName }
          : { projectId: undefined, projectName: undefined },
        actor,
      );
    } finally {
      setBusy(false);
    }
  };

  const onStatus = async (status: TaskStatus) => {
    if (status === task.status) return;
    setBusy(true);
    try {
      await setTaskStatus(task.id, status, actor);
    } finally {
      setBusy(false);
    }
  };

  const onAssignee = async (assignee: string) => {
    setBusy(true);
    try {
      await updateTask(task.id, { assignee: assignee || null }, actor);
    } finally {
      setBusy(false);
    }
  };

  const onPriority = async (priority: TaskPriority) => {
    setBusy(true);
    try {
      await updateTask(task.id, { priority }, actor);
    } finally {
      setBusy(false);
    }
  };

  const onDue = async (dueDate: string) => {
    setBusy(true);
    try {
      await updateTask(task.id, { dueDate: dueDate || null }, actor);
    } finally {
      setBusy(false);
    }
  };

  const onRecurrence = async (recurrence: TaskRecurrence) => {
    setBusy(true);
    try {
      await updateTask(task.id, { recurrence }, actor);
    } finally {
      setBusy(false);
    }
  };

  const onAddItem = async () => {
    const text = newItem.trim();
    if (!text) return;
    setBusy(true);
    try {
      await addChecklistItem(task.id, text, actor);
      setNewItem('');
    } finally {
      setBusy(false);
    }
  };

  const onToggle = async (itemId: string) => {
    await toggleChecklistItem(task.id, itemId);
  };

  const onRemove = async (itemId: string) => {
    await removeChecklistItem(task.id, itemId);
  };

  const onArchive = async () => {
    setBusy(true);
    try {
      await updateTask(task.id, { archived: true }, actor);
      onDeleted();
    } finally {
      setBusy(false);
    }
  };

  const timeline = [...task.timeline].reverse();
  const { done, total } = checklistProgress(task);

  return (
    // ClickUp-style drawer: the overlay hosts a full-viewport-height
    // drawer whose glass panel pins the header + footer actions and
    // scrolls ONLY its body region — so checklists / timeline / linked
    // work below the fold are always reachable (P0: "task detail cut
    // off at bottom"). Layout contract lives in lib/taskDetailLayout.
    <div role="dialog" aria-modal="true" onClick={onClose} style={TASK_DETAIL_OVERLAY}>
      <div onClick={(e) => e.stopPropagation()} style={TASK_DETAIL_DRAWER}>
        <GlassPanel variant="sharp" animate={false} style={TASK_DETAIL_PANEL}>
          {/* Pinned header: title, close, badges */}
          <div style={TASK_DETAIL_HEADER}>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <span className="t-eyebrow">Task</span>
                <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>
                  {task.title}
                </h2>
              </div>
              <button type="button" className="glass-btn" onClick={onClose} aria-label="Close">
                <X size={14} />
              </button>
            </div>

            {/* Badges */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
              <span className={PRIORITY_META[task.priority].pill}>
                {PRIORITY_META[task.priority].label}
              </span>
              <span className={STATUS_META[task.status].pill}>
                {STATUS_META[task.status].label}
              </span>
              {task.recurrence !== 'none' && (
                <span
                  className="glass-pill"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  <Repeat size={11} /> {RECURRENCE_META[task.recurrence]}
                </span>
              )}
            </div>
          </div>

          {/* Scrollable body: everything below the fold lives here */}
          <div style={TASK_DETAIL_BODY}>
          {/* Ask Argo — governed proposals (help / draft / complete) */}
          <ArgoAssistPanel
            task={task}
            allTasks={allTasks}
            linkedTicket={linkedTicket}
            canMutate={canMutate}
            actor={actor}
          />

          {/* Notes */}
          {task.notes && (
            <div style={{ marginTop: 16 }}>
              <span className="t-eyebrow">Notes</span>
              <p
                className="t-body"
                style={{ margin: '6px 0 0', color: C.textMuted, whiteSpace: 'pre-wrap' }}
              >
                {task.notes}
              </p>
            </div>
          )}

          {/* Link */}
          {(task.accountName || task.workflowName) && (
            <div style={{ marginTop: 16 }}>
              <span className="t-eyebrow">Linked project</span>
              <span
                className="t-body"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  marginTop: 2,
                  color: C.text,
                }}
              >
                <Link2 size={13} />
                {[task.accountName, task.workflowName].filter(Boolean).join(' · ')}
              </span>
            </div>
          )}

          {/* Linked engineering ticket + the roadmap milestones rolling
              this task up — the task↔ticket↔roadmap chain, visible from
              the task side. */}
          {(linkedTicket || task.ticketId || linkedMilestones.length > 0) && (
            <div style={{ marginTop: 16, display: 'grid', gap: 6 }}>
              <span className="t-eyebrow">Linked work</span>
              {task.ticketId && (
                <span
                  className="t-body"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: C.text }}
                >
                  <Link2 size={13} />
                  {linkedTicket
                    ? `${linkedTicket.id} · ${linkedTicket.title}`
                    : `${task.ticketId} (ticket not found)`}
                  {linkedTicket && (
                    <span className="glass-pill">{linkedTicket.status}</span>
                  )}
                </span>
              )}
              {linkedMilestones.map((m) => (
                <span
                  key={m.id}
                  className="t-meta"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: C.textMuted }}
                  title="This task is rolled up by a roadmap milestone — completing it moves the milestone's auto progress."
                >
                  <CalendarClock size={12} /> Roadmap: {m.title}
                </span>
              ))}
            </div>
          )}

          {/* Controls */}
          {canMutate && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 12,
                marginTop: 16,
              }}
            >
              <label style={{ display: 'grid', gap: 4 }}>
                <span className="t-eyebrow">Status</span>
                <select
                  className="glass-input"
                  value={task.status}
                  disabled={busy}
                  onChange={(e) => onStatus(e.target.value as TaskStatus)}
                >
                  {STATUS_ORDER.map((s) => (
                    <option key={s} value={s}>
                      {STATUS_META[s].label}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 4 }}>
                <span className="t-eyebrow">Assignee</span>
                <select
                  className="glass-input"
                  value={task.assignee ?? ''}
                  disabled={busy}
                  onChange={(e) => onAssignee(e.target.value)}
                >
                  <option value="">Unassigned</option>
                  {TASK_ASSIGNEES.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 4 }}>
                <span className="t-eyebrow">Priority</span>
                <select
                  className="glass-input"
                  value={task.priority}
                  disabled={busy}
                  onChange={(e) => onPriority(e.target.value as TaskPriority)}
                >
                  {PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {PRIORITY_META[p].label}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 4 }}>
                <span className="t-eyebrow">Due date</span>
                <input
                  className="glass-input"
                  type="date"
                  value={task.dueDate ?? ''}
                  disabled={busy}
                  onChange={(e) => onDue(e.target.value)}
                  style={{ color: overdue ? C.orangeWarm : undefined }}
                />
              </label>
              <label style={{ display: 'grid', gap: 4 }}>
                <span className="t-eyebrow">Recurrence</span>
                <select
                  className="glass-input"
                  value={task.recurrence}
                  disabled={busy}
                  onChange={(e) => onRecurrence(e.target.value as TaskRecurrence)}
                >
                  {RECURRENCES.map((r) => (
                    <option key={r} value={r}>
                      {RECURRENCE_META[r]}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 4 }}>
                <span className="t-eyebrow">Linked ticket</span>
                <select
                  className="glass-input"
                  value={task.ticketId ?? ''}
                  disabled={busy}
                  onChange={(e) => onTicket(e.target.value)}
                >
                  <option value="">None</option>
                  {tickets.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.id} · {t.title}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 4 }}>
                <span className="t-eyebrow">Project</span>
                <select
                  className="glass-input"
                  value={task.projectId ?? ''}
                  disabled={busy}
                  onChange={(e) => onProject(e.target.value)}
                >
                  <option value="">No project</option>
                  {[...projects]
                    .sort((a, b) => a.clientName.localeCompare(b.clientName) || a.title.localeCompare(b.title))
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.clientName} · {p.title}
                      </option>
                    ))}
                </select>
              </label>
            </div>
          )}

          <TaskCollabPanel task={task} actor={actor} canMutate={canMutate} />

          {/* Checklist */}
          <div style={{ marginTop: 20 }}>
            <span
              className="t-eyebrow"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <ListChecks size={12} /> Subtasks
              {total > 0 && (
                <span className="t-meta" style={{ color: C.textMuted }}>
                  {done}/{total}
                </span>
              )}
            </span>
            <div style={{ display: 'grid', gap: 6, margin: '8px 0 0' }}>
              {task.checklist.length === 0 && (
                <span className="t-meta">No subtasks yet.</span>
              )}
              {task.checklist.map((item) => (
                <div
                  key={item.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  <button
                    type="button"
                    className="glass-btn"
                    onClick={() => onToggle(item.id)}
                    disabled={!canMutate}
                    aria-label={item.done ? 'Mark incomplete' : 'Mark complete'}
                    style={{
                      padding: 4,
                      flex: '0 0 auto',
                      color: item.done ? C.green : C.textMuted,
                    }}
                  >
                    {item.done ? <CheckSquare size={15} /> : <Square size={15} />}
                  </button>
                  <span
                    className="t-body"
                    style={{
                      flex: 1,
                      minWidth: 0,
                      color: item.done ? C.textDim : C.text,
                      textDecoration: item.done ? 'line-through' : undefined,
                    }}
                  >
                    {item.text}
                  </span>
                  {canMutate && (
                    <button
                      type="button"
                      className="glass-btn"
                      onClick={() => onRemove(item.id)}
                      aria-label="Remove subtask"
                      style={{ padding: 4, flex: '0 0 auto', color: C.textMuted }}
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {canMutate && (
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <input
                  className="glass-input"
                  style={{ flex: 1 }}
                  placeholder="Add a subtask…"
                  value={newItem}
                  disabled={busy}
                  onChange={(e) => setNewItem(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      onAddItem();
                    }
                  }}
                />
                <button
                  type="button"
                  className="glass-btn glass-btn--primary"
                  onClick={onAddItem}
                  disabled={busy || !newItem.trim()}
                >
                  <Plus size={13} />
                </button>
              </div>
            )}
          </div>

          {/* Meta */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 12,
              marginTop: 16,
            }}
          >
            <Meta label="Created by" value={task.createdBy ?? '—'} />
            <Meta label="Updated" value={fmtRelative(task.updatedAt)} />
            {task.completedAt && (
              <Meta label="Completed" value={fmtRelative(task.completedAt)} />
            )}
          </div>

          {/* Timeline */}
          <div style={{ marginTop: 20 }}>
            <span className="t-eyebrow">Timeline</span>
            <ol
              style={{
                listStyle: 'none',
                margin: '8px 0 0',
                padding: 0,
                display: 'grid',
                gap: 10,
              }}
            >
              {timeline.map((e) => (
                <li
                  key={e.id}
                  style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: 99,
                      marginTop: 6,
                      flex: '0 0 auto',
                      background: C.magenta,
                    }}
                  />
                  <span style={{ minWidth: 0 }}>
                    <span className="t-body" style={{ color: C.text }}>
                      {e.text}
                    </span>
                    <span className="t-meta" style={{ display: 'block' }}>
                      {fmtRelative(e.at)} · {e.actor}
                    </span>
                  </span>
                </li>
              ))}
            </ol>
          </div>

          </div>

          {/* Pinned footer: actions stay reachable without scrolling */}
          {canMutate && (
            <div style={TASK_DETAIL_FOOTER}>
              <button
                type="button"
                className="glass-btn"
                onClick={onArchive}
                disabled={busy}
                title="Archive task — hidden from the board, record kept"
                style={{ color: C.textMuted }}
              >
                <Archive size={13} /> Archive task
              </button>
            </div>
          )}
        </GlassPanel>
      </div>
    </div>
  );
}

// --- Ask Argo (agentic affordances) ----------------------------------------

/**
 * The task's agentic surface: "Ask Argo to help / draft / complete".
 *
 * Every button produces a GOVERNED PROPOSAL — Argo never writes anything.
 * When the real agent-worker is configured (VITE_AGENT_API_URL) the
 * proposal is reasoned by Claude; otherwise it is compiled
 * deterministically from the task's own signals and honestly labelled as
 * such. The operator then applies each part as a normal, timeline-logged
 * task edit: add steps as subtasks, save the draft to notes, mark done.
 */
function ArgoAssistPanel({
  task,
  allTasks,
  linkedTicket,
  canMutate,
  actor,
}: {
  task: ProjectTask;
  allTasks: ProjectTask[];
  linkedTicket: Ticket | null;
  canMutate: boolean;
  actor: string;
}) {
  const [busyMode, setBusyMode] = useState<AssistMode | null>(null);
  const [result, setResult] = useState<(TaskAssist & { mode: AssistMode }) | null>(null);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState<string | null>(null);

  const workerConfigured = argoWorker.isConfigured();

  // Live per-task hint — same pure engine as the board cards, but here it
  // also sees the linked ticket, and its `mode` pre-picks the right button.
  const hint = useMemo(
    () =>
      suggestForTask(task, {
        allTasks,
        ticket: linkedTicket
          ? { id: linkedTicket.id, status: linkedTicket.status, title: linkedTicket.title }
          : null,
      }),
    [task, allTasks, linkedTicket],
  );

  /** Real worker seam: parse-or-throw → requestAssist degrades to fallback. */
  const runWorker = useMemo(
    () =>
      workerConfigured
        ? async (prompt: string) => {
            const out = await argoWorker.runAgent(
              {
                id: 'task-assist',
                name: 'Task Assist',
                template: 'task-router',
                persona: 'ops',
              },
              { prompt },
            );
            return JSON.parse(out.text) as unknown;
          }
        : undefined,
    [workerConfigured],
  );

  const ask = async (mode: AssistMode) => {
    if (busyMode) return;
    setBusyMode(mode);
    setApplied(null);
    try {
      // Never rejects: worker failures degrade to the deterministic fallback.
      const assist = await requestAssist(task, mode, runWorker ? { runWorker } : {});
      setResult({ ...assist, mode });
    } finally {
      setBusyMode(null);
    }
  };

  // Governed applies — each one is a normal task edit, logged to the
  // timeline like any manual change. Nothing happens until clicked.
  const stepsToAdd = useMemo(
    () => (result ? newChecklistSteps(result.steps, task.checklist) : []),
    [result, task.checklist],
  );

  const applySteps = async () => {
    if (!result || stepsToAdd.length === 0) return;
    setApplying(true);
    try {
      for (const s of stepsToAdd) await addChecklistItem(task.id, s, `${actor} (via Argo)`);
      setApplied(`Added ${stepsToAdd.length} subtask${stepsToAdd.length === 1 ? '' : 's'}.`);
    } finally {
      setApplying(false);
    }
  };

  const applyDraft = async () => {
    if (!result?.draft) return;
    setApplying(true);
    try {
      const notes = task.notes ? `${task.notes}\n\n---\n\n${result.draft}` : result.draft;
      await updateTask(task.id, { notes }, `${actor} (via Argo)`);
      setApplied('Draft saved to notes.');
    } finally {
      setApplying(false);
    }
  };

  const applyComplete = async () => {
    setApplying(true);
    try {
      await setTaskStatus(task.id, 'done', `${actor} (via Argo)`);
      setApplied('Task marked done.');
    } finally {
      setApplying(false);
    }
  };

  const MODES: { mode: AssistMode; label: string; icon: LucideIcon }[] = [
    { mode: 'help', label: 'Ask Argo to help', icon: Sparkles },
    { mode: 'draft', label: 'Draft it', icon: PenLine },
    { mode: 'complete', label: 'Complete it', icon: CheckCircle2 },
  ];

  return (
    <div
      style={{
        marginTop: 16,
        padding: 12,
        borderRadius: 12,
        border: `1px solid ${C.magenta}33`,
        background: `${C.magenta}0d`,
      }}
    >
      <span
        className="t-eyebrow"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
      >
        <Sparkles size={12} style={{ color: C.magenta }} /> Ask Argo
        <span
          className={workerConfigured ? 'glass-pill glass-pill--green' : 'glass-pill'}
          style={{ fontSize: '0.65rem' }}
          title={
            workerConfigured
              ? 'Live agent-worker connected — proposals are reasoned by Claude.'
              : 'No agent-worker configured (VITE_AGENT_API_URL) — proposals are compiled deterministically from this task\u2019s signals.'
          }
        >
          {workerConfigured ? 'Live worker' : 'Worker offline'}
        </span>
      </span>

      {/* Argo suggests — the same live-signal hint as the board card */}
      {hint && (
        <p
          className="t-meta"
          style={{
            margin: '8px 0 0',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 6,
            color: C.magenta,
          }}
        >
          <Sparkles size={11} style={{ flex: '0 0 auto', marginTop: 2 }} />
          <span>
            Argo suggests: {hint.text}{' '}
            <button
              type="button"
              className="glass-btn"
              onClick={() => ask(hint.mode)}
              disabled={busyMode !== null}
              style={{ padding: '1px 8px', fontSize: '0.7rem', marginLeft: 4 }}
            >
              {ASSIST_MODE_LABEL[hint.mode]}
            </button>
          </span>
        </p>
      )}

      {/* The three affordances */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
        {MODES.map(({ mode, label, icon: Icon }) => (
          <button
            key={mode}
            type="button"
            className="glass-btn"
            onClick={() => ask(mode)}
            disabled={busyMode !== null}
            title={ASSIST_MODE_LABEL[mode]}
          >
            {busyMode === mode ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <Icon size={13} />
            )}{' '}
            {label}
          </button>
        ))}
      </div>
      <p className="t-meta" style={{ margin: '8px 0 0', color: C.textDim }}>
        Argo proposes; you apply. Nothing is written to the task until you
        accept a proposal below — every apply is a normal edit logged to the
        timeline.
      </p>

      {/* The governed proposal */}
      {result && (
        <div
          style={{
            marginTop: 12,
            padding: 12,
            borderRadius: 10,
            border: `1px solid ${C.magenta}26`,
            background: 'rgba(0,0,0,0.18)',
            display: 'grid',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span className="t-eyebrow">
              Proposal · {ASSIST_MODE_LABEL[result.mode]}
            </span>
            <span
              className={
                result.source === 'worker'
                  ? 'glass-pill glass-pill--green'
                  : 'glass-pill glass-pill--orange'
              }
              style={{ fontSize: '0.65rem' }}
              title={
                result.source === 'worker'
                  ? 'Reasoned by the live Claude agent-worker.'
                  : 'Compiled deterministically from this task\u2019s signals — no model involved.'
              }
            >
              {result.source === 'worker' ? 'Argo worker' : 'Deterministic fallback'}
            </span>
            <button
              type="button"
              className="glass-btn"
              onClick={() => {
                setResult(null);
                setApplied(null);
              }}
              aria-label="Dismiss proposal"
              style={{ marginLeft: 'auto', padding: 4 }}
            >
              <X size={12} />
            </button>
          </div>

          {result.source === 'fallback' && !workerConfigured && (
            <p className="t-meta" style={{ margin: 0, color: C.orangeWarm }}>
              The live Argo worker isn&apos;t configured (set VITE_AGENT_API_URL),
              so this proposal was compiled from the task&apos;s own signals.
              Connect the worker for reasoned drafts.
            </p>
          )}

          <p className="t-body" style={{ margin: 0, color: C.text }}>
            {result.summary}
          </p>

          {result.steps.length > 0 && (
            <ol style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 4 }}>
              {result.steps.map((s, i) => (
                <li key={i} className="t-body" style={{ color: C.textMuted }}>
                  {s}
                </li>
              ))}
            </ol>
          )}

          {result.draft && (
            <pre
              className="t-meta"
              style={{
                margin: 0,
                padding: 10,
                borderRadius: 8,
                background: 'rgba(0,0,0,0.25)',
                color: C.textMuted,
                whiteSpace: 'pre-wrap',
                maxHeight: 220,
                overflowY: 'auto',
              }}
            >
              {result.draft}
            </pre>
          )}

          {canMutate && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {result.steps.length > 0 && (
                <button
                  type="button"
                  className="glass-btn glass-btn--primary"
                  onClick={applySteps}
                  disabled={applying || stepsToAdd.length === 0}
                  title={
                    stepsToAdd.length === 0
                      ? 'Every proposed step is already on the checklist.'
                      : undefined
                  }
                >
                  <ListChecks size={13} />{' '}
                  {stepsToAdd.length === 0
                    ? 'Steps already on checklist'
                    : `Add ${stepsToAdd.length} step${stepsToAdd.length === 1 ? '' : 's'} as subtasks`}
                </button>
              )}
              {result.draft && (
                <button
                  type="button"
                  className="glass-btn glass-btn--primary"
                  onClick={applyDraft}
                  disabled={applying}
                >
                  <PenLine size={13} /> Save draft to notes
                </button>
              )}
              {result.mode === 'complete' && task.status !== 'done' && (
                <button
                  type="button"
                  className="glass-btn glass-btn--primary"
                  onClick={applyComplete}
                  disabled={applying}
                >
                  <CheckCircle2 size={13} /> Mark done
                </button>
              )}
            </div>
          )}

          {applied && (
            <p
              className="t-meta"
              style={{
                margin: 0,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                color: C.green,
              }}
            >
              <CheckCircle2 size={12} /> {applied}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="t-eyebrow">{label}</span>
      <span className="t-body" style={{ display: 'block', marginTop: 2, color: C.text }}>
        {value}
      </span>
    </div>
  );
}

// --- Task collaboration + time ----------------------------------------------

/** Format logged minutes as "Xh Ym". */
function fmtMinutes(m: number): string {
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
}

function TaskCollabPanel({
  task,
  actor,
  canMutate,
}: {
  task: ProjectTask;
  actor: string;
  canMutate: boolean;
}) {
  const [commentBody, setCommentBody] = useState('');
  const [attLabel, setAttLabel] = useState('');
  const [attUrl, setAttUrl] = useState('');
  const [mins, setMins] = useState('');
  const [timeNote, setTimeNote] = useState('');

  const candidates = useMemo(() => {
    const set = new Set<string>([...TASK_ASSIGNEES]);
    if (task.assignee) set.add(task.assignee);
    return [...set];
  }, [task.assignee]);

  const comments = task.comments ?? [];
  const attachments = task.attachments ?? [];
  const entries = task.timeEntries ?? [];
  const totalMin = entries.reduce((s, e) => s + e.minutes, 0);
  const previewMentions = parseMentions(commentBody, candidates);

  const eyebrow: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6 };

  return (
    <div style={{ marginTop: 20, display: 'grid', gap: 18 }}>
      {/* Time */}
      <div>
        <span className="t-eyebrow" style={eyebrow}>
          <Clock size={12} /> Time
          {totalMin > 0 && <span className="t-meta" style={{ color: C.textMuted }}>{fmtMinutes(totalMin)} total</span>}
        </span>
        <div style={{ display: 'grid', gap: 6, margin: '8px 0 0' }}>
          {entries.length === 0 && <span className="t-meta">No time logged yet.</span>}
          {entries.map((e) => (
            <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.textMuted }}>
              <span style={{ color: C.text, fontWeight: 600 }}>{fmtMinutes(e.minutes)}</span>
              <span>· {e.who}</span>
              {e.note && <span style={{ color: C.textDim }}>· {e.note}</span>}
              {canMutate && (
                <button type="button" onClick={() => void removeTaskTimeEntry(task.id, e.id)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: C.textDim, cursor: 'pointer', display: 'inline-flex' }}>
                  <X size={11} />
                </button>
              )}
            </div>
          ))}
          {canMutate && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <input className="glass-input" style={{ width: 90 }} type="number" min="1" placeholder="minutes" value={mins} onChange={(e) => setMins(e.target.value)} />
              <input className="glass-input" style={{ flex: 1, minWidth: 120 }} placeholder="note (optional)" value={timeNote} onChange={(e) => setTimeNote(e.target.value)} />
              <button
                type="button"
                className="glass-btn"
                disabled={!(parseInt(mins, 10) > 0)}
                style={{ opacity: parseInt(mins, 10) > 0 ? 1 : 0.5 }}
                onClick={() => {
                  const m = parseInt(mins, 10);
                  if (!(m > 0)) return;
                  void addTaskTimeEntry(task.id, { who: actor, minutes: m, note: timeNote.trim() || undefined });
                  setMins('');
                  setTimeNote('');
                }}
              >
                Log
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Attachments */}
      <div>
        <span className="t-eyebrow" style={eyebrow}>
          <Paperclip size={12} /> Attachments {attachments.length > 0 && <span className="t-meta" style={{ color: C.textMuted }}>{attachments.length}</span>}
        </span>
        <div style={{ display: 'grid', gap: 6, margin: '8px 0 0' }}>
          {attachments.length === 0 && <span className="t-meta">No attachments yet.</span>}
          {attachments.map((a) => (
            <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <a href={a.url} target="_blank" rel="noreferrer" style={{ color: C.blueLight, textDecoration: 'none' }}>{a.label}</a>
              <span className="t-meta" style={{ color: C.textDim }}>· {a.addedBy}</span>
              {canMutate && (
                <button type="button" onClick={() => void removeTaskAttachment(task.id, a.id)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: C.textDim, cursor: 'pointer', display: 'inline-flex' }}>
                  <X size={11} />
                </button>
              )}
            </div>
          ))}
          {canMutate && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <FileAttachButton scope="task" id={task.id} addedBy={actor} onAttach={(att) => addTaskAttachment(task.id, att)} />
              <span style={{ fontSize: 11, color: C.textDim }}>or link:</span>
              <input className="glass-input" style={{ width: 100 }} placeholder="label" value={attLabel} onChange={(e) => setAttLabel(e.target.value)} />
              <input className="glass-input" style={{ flex: 1, minWidth: 120 }} placeholder="https://…" value={attUrl} onChange={(e) => setAttUrl(e.target.value)} />
              <button
                type="button"
                className="glass-btn"
                disabled={!attLabel.trim() || !attUrl.trim()}
                style={{ opacity: !attLabel.trim() || !attUrl.trim() ? 0.5 : 1 }}
                onClick={() => {
                  void addTaskAttachment(task.id, { kind: 'link', label: attLabel.trim(), url: attUrl.trim(), addedBy: actor });
                  setAttLabel('');
                  setAttUrl('');
                }}
              >
                Add link
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Comments */}
      <div>
        <span className="t-eyebrow" style={eyebrow}>
          <MessageSquare size={12} /> Comments {comments.length > 0 && <span className="t-meta" style={{ color: C.textMuted }}>{comments.length}</span>}
        </span>
        <div style={{ display: 'grid', gap: 8, margin: '8px 0 0' }}>
          {comments.length === 0 && <span className="t-meta">No comments yet. Use @ to mention a teammate.</span>}
          {comments.map((c) => (
            <div key={c.id} style={{ display: 'grid', gap: 3, padding: '6px 8px', borderRadius: 8, background: 'rgba(255,255,255,0.03)' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{c.authorName}</span>
                <span style={{ fontSize: 10, color: C.textDim }}>{new Date(c.createdAt).toLocaleString()}</span>
              </div>
              <span style={{ fontSize: 13, color: C.text, whiteSpace: 'pre-wrap' }}>{c.body}</span>
              {c.mentions.length > 0 && (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {c.mentions.map((m) => (
                    <span key={m} style={{ fontSize: 10, color: C.magentaSoft, background: 'rgba(255,0,255,0.10)', padding: '1px 6px', borderRadius: 999 }}>@{m}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
          {canMutate && (
            <div style={{ display: 'grid', gap: 6 }}>
              <MentionTextarea className="glass-input" rows={2} placeholder="Write a comment… @Name to mention" value={commentBody} onChange={setCommentBody} candidates={candidates} style={{ resize: 'vertical', width: '100%' }} />
              {previewMentions.length > 0 && (
                <span className="t-meta" style={{ color: C.textMuted }}>Mentions: {previewMentions.map((m) => `@${m}`).join(', ')}</span>
              )}
              <div>
                <button
                  type="button"
                  className="glass-btn"
                  disabled={!commentBody.trim()}
                  style={{ opacity: commentBody.trim() ? 1 : 0.5, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  onClick={() => {
                    const body = commentBody.trim();
                    if (!body) return;
                    const mentions = parseMentions(body, candidates);
                    void addTaskComment(task.id, actor, body, mentions);
                    void queueMentionEmails({
                      mentions,
                      authorName: actor,
                      context: `the task "${task.title}"`,
                    });
                    setCommentBody('');
                  }}
                >
                  <Send size={12} /> Comment
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// --- New task form ----------------------------------------------------------

function TaskForm({
  createdBy,
  projects,
  onClose,
  onCreated,
}: {
  createdBy: string;
  projects: Project[];
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [assignee, setAssignee] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [recurrence, setRecurrence] = useState<TaskRecurrence>('none');
  const [projectId, setProjectId] = useState('');
  const [checklist, setChecklist] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const t = title.trim();
    if (!t) return;
    setBusy(true);
    try {
      const proj = projects.find((p) => p.id === projectId);
      const created = await createTask({
        title: t,
        notes: notes.trim() || undefined,
        priority,
        assignee: assignee || null,
        dueDate: dueDate || null,
        recurrence,
        projectId: proj?.id,
        projectName: proj?.title,
        accountName: proj?.clientName,
        checklist: checklist
          .split('\n')
          .map((l) => l.trim())
          .filter(Boolean)
          .map((text) => ({ text })),
        createdBy,
      });
      onCreated(created.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 90,
        background: 'rgba(6, 6, 12, 0.55)',
        backdropFilter: 'blur(3px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: 24,
        overflowY: 'auto',
      }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(560px, 100%)', marginTop: 24 }}>
        <GlassPanel variant="sharp" animate={false} style={{ padding: '24px 26px' }}>
          <div
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <h2 className="t-section-title" style={{ margin: 0 }}>
              New task
            </h2>
            <button type="button" className="glass-btn" onClick={onClose} aria-label="Close">
              <X size={14} />
            </button>
          </div>

          <div style={{ display: 'grid', gap: 12, marginTop: 16 }}>
            <label style={{ display: 'grid', gap: 4 }}>
              <span className="t-eyebrow">Title</span>
              <input
                className="glass-input"
                placeholder="Short, action-oriented title"
                value={title}
                autoFocus
                onChange={(e) => setTitle(e.target.value)}
              />
            </label>

            <label style={{ display: 'grid', gap: 4 }}>
              <span className="t-eyebrow">Notes</span>
              <textarea
                className="glass-input"
                rows={3}
                placeholder="Context, details, links…"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                style={{ resize: 'vertical' }}
              />
            </label>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <label style={{ display: 'grid', gap: 4 }}>
                <span className="t-eyebrow">Priority</span>
                <select
                  className="glass-input"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as TaskPriority)}
                >
                  {PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {PRIORITY_META[p].label}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 4 }}>
                <span className="t-eyebrow">Assignee</span>
                <select
                  className="glass-input"
                  value={assignee}
                  onChange={(e) => setAssignee(e.target.value)}
                >
                  <option value="">Unassigned</option>
                  {TASK_ASSIGNEES.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 4 }}>
                <span className="t-eyebrow">Due date</span>
                <input
                  className="glass-input"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </label>
              <label style={{ display: 'grid', gap: 4 }}>
                <span className="t-eyebrow">Recurrence</span>
                <select
                  className="glass-input"
                  value={recurrence}
                  onChange={(e) => setRecurrence(e.target.value as TaskRecurrence)}
                >
                  {RECURRENCES.map((r) => (
                    <option key={r} value={r}>
                      {RECURRENCE_META[r]}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label style={{ display: 'grid', gap: 4 }}>
              <span className="t-eyebrow">Project (optional)</span>
              <select
                className="glass-input"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
              >
                <option value="">No project</option>
                {[...projects]
                  .sort((a, b) => a.clientName.localeCompare(b.clientName) || a.title.localeCompare(b.title))
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.clientName} · {p.title}
                    </option>
                  ))}
              </select>
            </label>

            <label style={{ display: 'grid', gap: 4 }}>
              <span className="t-eyebrow">Subtasks (one per line)</span>
              <textarea
                className="glass-input"
                rows={3}
                placeholder={'Pull data\nDraft v1\nReview'}
                value={checklist}
                onChange={(e) => setChecklist(e.target.value)}
                style={{ resize: 'vertical' }}
              />
            </label>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
            <button type="button" className="glass-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="glass-btn glass-btn--primary"
              onClick={submit}
              disabled={busy || !title.trim()}
            >
              <Send size={13} /> Create task
            </button>
          </div>
        </GlassPanel>
      </div>
    </div>
  );
}
